import React from 'react'
import ButtonComponent from '../../components/ButtonComponent';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyCartApp = () => {
    const navigate = useNavigate();
    const handleBackToPolicyPage = () => {
        navigate('/documents');
    }
    return (
        <>
            <div className="container mt-5 privacy-policy"><ButtonComponent title='Back to Policy Page' backgroundColor=' #590B21' onClick={handleBackToPolicyPage}/></div>
            <div className="container mt-2 mb-5 py-4 privacy-policy">
                <h3 className='fw-bold'>Privacy Policy</h3>
                <p>Effective Date: 1st May 2025</p>
                <p>
                    Welcome to Curry King (Private) Limited ("Curry King", "we", "our", or "us"). We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us.
                </p>
                <p>By using our website or services, you consent to the practices described in this Privacy Policy.</p>
                <hr />
                <h5 className='fw-bold'>1. Information We Collect</h5>
                <p>We may collect the following types of information:</p>
                <ul>
                    <li>
                        <strong>Personal Information:</strong> Name, email address, phone number, delivery address, payment details, and other information you provide when placing an order or registering an account.
                    </li>
                    <li>
                        <strong>Device and Usage Information:</strong> IP address, browser type, operating system, access times, pages viewed, app version, and basic device identifiers (such as IDFV on iOS) used for measurement and to improve performance.
                    </li>
                    <li>
                        <strong>Location Information:</strong> For delivery partners using our Delivery Partners App, we collect location data to enable real-time order tracking and delivery operations.
                        <ul>
                            <li><strong>Foreground Location:</strong> Collected while the app is actively in use.</li>
                            <li><strong>Background Location:</strong> Collected while the app is running in the background during an active delivery.</li>
                        </ul>
                        <p>Background location access is required to:</p>
                        <ul>
                            <li>Enable customers to track their orders in real time.</li>
                            <li>Provide accurate estimated arrival times (ETA).</li>
                            <li>Support navigation and delivery completion.</li>
                            <li>Ensure service reliability when delivery partners switch between apps (e.g., navigation or phone calls).</li>
                        </ul>
                        <p>Location data is:</p>
                        <ul>
                            <li>Collected only when a delivery partner is actively engaged in a delivery.</li>
                            <li>Automatically stopped once the delivery is completed.</li>
                            <li>Not used for advertising or marketing purposes.</li>
                            <li>Not shared with third-party advertisers.</li>
                        </ul>
                        <p>
                            Delivery partners can control location permissions through their device settings at any time. However, disabling location access may prevent order tracking and delivery functionality.
                        </p>
                    </li>
                    <li>
                        <strong>Cookies and Similar Technologies:</strong> We use cookies/local storage and SDKs for essential operations (such as keeping you signed in and maintaining session state) and analytics to understand how our services are used. We do not use third-party advertising cookies or SDKs, and we do not collect the iOS advertising identifier (IDFA).
                    </li>
                    <li>
                        <strong>Authentication & Analytics Providers:</strong> We use Firebase Authentication (for integrating third-party sign-in) and analytic tools like Google Analytics (for aggregate, measurement-only analytics). We do not enable advertising features, and we do not use analytics data for third-party advertising.
                    </li>
                </ul>
                <hr />
                <h5 className='fw-bold'>2. How We Use Your Information</h5>
                <p>We use the information we collect to:</p>
                <ul>
                    <li>Process and fulfill orders.</li>
                    <li>Enable real-time delivery tracking.</li>
                    <li>Calculate delivery times and optimize routes.</li>
                    <li>Communicate regarding orders, accounts, and support inquiries.</li>
                    <li>Provide authentication, security, and fraud prevention.</li>
                    <li>Measure usage and improve performance and reliability (analytics in aggregate).</li>
                    <li>Send marketing communications only if you have opted in.</li>
                    <li>Comply with legal obligations.</li>
                </ul>
                <p>
                    <strong>No third-party ad tracking:</strong> We do not link your data with third-party data for targeted advertising and do not share data with data brokers. As a result, we do not request App Tracking Transparency (ATT) permission on iOS.
                </p>
                <hr />
                <h5 className='fw-bold'>3. Sharing Your Information</h5>
                <p>We may share your information with:</p>
                <ul>
                    <li>
                        <strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (e.g., Firebase Authentication, Google Analytics for Firebase, cloud hosting/IT support, payment processing, and delivery tracking infrastructure). These providers process data under contractual confidentiality and security obligations.
                    </li>
                    <li>
                        <strong>Customers (Limited to Delivery Tracking):</strong> During an active delivery, a delivery partner's live location may be shared with the relevant customer solely for the purpose of tracking their order.
                    </li>
                    <li>
                        <strong>Legal Requirements:</strong> If required by law, regulation, or legal process.
                    </li>
                    <li>
                        <strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition.
                    </li>
                </ul>
                <p>We do not sell, rent, or trade your personal information to third parties.</p>
                <hr />
                <h5 className='fw-bold'>4. Your Rights and Choices</h5>
                <p>You have the right to:</p>
                <ul>
                    <li>Access, update, or delete your personal information.</li>
                    <li>Opt out of receiving marketing communications.</li>
                    <li>Request restriction or objection to certain processing activities, where applicable.</li>
                </ul>
                <p>
                    To exercise your rights, please contact us using the details provided below. You can also manage app permissions (camera, photos, notifications, location) in your device settings.
                </p>
                <hr />
                <h5 className='fw-bold'>5. Data Security</h5>
                <p>
                    We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction (e.g., HTTPS, access controls, secure hosting). However, no method of transmission over the Internet or electronic storage is 100% secure.
                </p>
                <hr />
                <h5 className='fw-bold'>6. Retention of Your Information</h5>
                <p>
                    We retain personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy and as required by applicable legal, accounting, or reporting obligations. When no longer required, we delete or de-identify the data.
                </p>
                <p>
                    Location data collected during deliveries is retained only as long as necessary for operational, support, fraud prevention, and legal compliance purposes.
                </p>
                <hr />
                <h5 className='fw-bold'>7. Children's Privacy</h5>
                <p>
                    Our services are intended for use by adults. All orders and delivery partner registrations must be completed by individuals aged 18 and above. We do not knowingly collect personal information directly from children under 13.
                </p>
                <hr />
                <h5 className='fw-bold'>8. Changes to This Privacy Policy</h5>
                <p>
                    We may update this Privacy Policy from time to time. We encourage you to review this policy periodically. Continued use of our services after changes indicates acceptance of the updated policy.
                </p>
                <hr />
                <h5 className='fw-bold'>9. Contact Us</h5>
                <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us at:</p>
                <strong>Curry King (Private) Limited</strong>
                <br />
                55/2 Vauxhall Lane, Colombo 2, Sri Lanka
                <br />
                <strong>Email:</strong> regulatory@curryking.lk
                <hr />
            </div>
        </>
    )
}

export default PrivacyPolicyCartApp;